import {
    PoleDashboardViewModel,
    ServiceScoreViewModel,
    ServiceViewModel,
    VALUE_CODES,
    VALUE_LABELS,
    ValueCode,
    ValueScoreViewModel
} from "../models/OverviewDashboard";
import OrganizationService from "./OrganizationService";
import ValueMeasureService from "./MeasureService";
import { calculatePoleScores } from "./PoleStatsCalculator";
import {
    calculateAverageScore,
    compareServicePeriods,
    ServicePeriodStatistics
} from "./ServiceStatsComparator";
import { UpCareStat } from "../utils/UpCareStats";
import { getPreviousCalendarMonthPeriod } from "../utils/StatisticsPeriod";

export interface OverviewDashboardServiceConfig {
    fhirBaseUrl: string;
    measureId: string;
    periodStart: string;
    periodEnd: string;
}

class OverviewDashboardService {
    private organizationService: OrganizationService;
    private currentMeasureService: ValueMeasureService;
    private previousMeasureService: ValueMeasureService;

    constructor(config: OverviewDashboardServiceConfig) {
        this.organizationService = new OrganizationService({
            fhirBaseUrl: config.fhirBaseUrl
        });

        this.currentMeasureService = new ValueMeasureService(config);

        const previousPeriod = getPreviousCalendarMonthPeriod(config.periodStart);
        this.previousMeasureService = new ValueMeasureService({
            ...config,
            periodStart: previousPeriod.periodStart,
            periodEnd: previousPeriod.periodEnd
        });
    }

    async load(poleIdentifier: string): Promise<PoleDashboardViewModel> {
        const poleNode = await this.organizationService.getOrganizationByIdentifier(
            poleIdentifier
        );
        const childNodes = await this.organizationService.getChildOrganizations(
            poleNode.reference
        );

        const services: ServiceViewModel[] = childNodes.map((child) => ({
            id: child.id,
            name: child.name,
            reference: child.reference,
            poleReference: poleNode.reference
        }));

        const serviceScores = await Promise.all(
            services.map((service) => this.loadServiceStatistics(service))
        );

        return {
            pole: {
                id: poleNode.id,
                name: poleNode.name,
                reference: poleNode.reference
            },
            services: serviceScores,
            poleScores: calculatePoleScores(serviceScores)
        };
    }

    private async loadServiceStatistics(service: ServiceViewModel): Promise<ServiceScoreViewModel> {
        const [currentResult, previousResult] = await Promise.allSettled([
            this.currentMeasureService.evaluate(service.reference),
            this.previousMeasureService.evaluate(service.reference)
        ]);

        if (currentResult.status === "rejected") {
            console.error(
                `Impossible de charger les statistiques courantes de ${service.name}.`,
                currentResult.reason
            );
        }

        if (previousResult.status === "rejected") {
            console.error(
                `Impossible de charger les statistiques précédentes de ${service.name}.`,
                previousResult.reason
            );
        }

        const current = this.toPeriodStatistics(
            currentResult.status === "fulfilled" ? currentResult.value : []
        );
        const previous = this.toPeriodStatistics(
            previousResult.status === "fulfilled" ? previousResult.value : []
        );

        return {
            service,
            values: current.values,
            averageScore: current.averageScore,
            ...compareServicePeriods(current, previous)
        };
    }

    private toPeriodStatistics(stats: UpCareStat[]): ServicePeriodStatistics {
        const values = VALUE_CODES.reduce((result, code) => {
            const statId = this.getStatId(code);
            const stat = stats.find((item) => item.id === statId);
            const hasValue = typeof stat?.rawValue === "number" && Number.isFinite(stat.rawValue);

            result[code] = {
                code,
                label: VALUE_LABELS[code],
                score: hasValue ? stat.rawValue : undefined,
                percentage: hasValue && typeof stat.percent === "number" ? stat.percent : undefined,
                status: hasValue ? "available" : "no-data"
            };
            return result;
        }, {} as Record<ValueCode, ValueScoreViewModel>);

        return {
            values,
            averageScore: calculateAverageScore(values)
        };
    }

    private getStatId(code: ValueCode): string {
        const mapping: Record<ValueCode, string> = {
            bienveillance: "average-bienveillance",
            respect: "average-respect",
            "faire-equipe": "average-faire-equipe",
            engagement: "average-engagement"
        };

        return mapping[code];
    }
}

export default OverviewDashboardService;
